// src/screens/StreamOverlayScreen.js
// OBS / Streamlabs Browser Source overlay for live event data.
// Rendered outside all app wrappers (no NavigationContainer, no CookieConsent, no auth).
// Event overlay: /overlay?id=XXX&collection=tournaments&accent=FCD000&theme=dark&position=bottomleft&scale=1
//   Registration open → signups, full pot, current pot, countdown
//   Registration closed → "Racing for $X" (1st place prize)
// Subscriber overlay: /overlay?type=subs&channel=<handle>&goal=25&accent=FCD000&position=bottomleft&scale=1
//   Live counter off public_profiles.followerCount (mirrored there by
//   toggleStreamFollow precisely so this unauthenticated page can listen);
//   optional goal meter; flashes NEW SUBSCRIBER when the count ticks up.
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { db } from '../../firebaseConfig';
import { doc, onSnapshot, collection, query, where, limit, getDocs } from 'firebase/firestore';
import { fmtUSD, toDateLike } from '../utils/eventHelper';
import { fmtCrits } from '../utils/currencyHelper';
import { PAYOUT_STRUCTURES } from '../constants/payoutStructures';
import { resolvePayoutStructureId } from '../hooks/usePayouts';
import useCritsEnabled from '../hooks/useCritsEnabled';

const DEFAULT_ACCENT = '#FCD000';

const POS_MAP = {
  topleft:     { top: 20, left: 20 },
  topright:    { top: 20, right: 20 },
  bottomleft:  { bottom: 20, left: 20 },
  bottomright: { bottom: 20, right: 20 },
};

/* ---------- Countdown hook ---------- */
function useCountdown(untilDate) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const target = untilDate?.getTime?.() ?? null;
  const ms = Math.max(0, (target ?? 0) - now);
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return { done: !target || ms <= 0, d, h, m, sec };
}

/* ---------- 1st place prize calculator ---------- */
// isCrit → round the champion's share UP (Crits are a non-monetary scoreboard);
// cash rounds to nearest, matching the rest of the app.
function getFirstPlaceCents(ev, potCents, isCrit = false) {
  if (!ev || !potCents) return 0;
  const isTournament = (ev.mode || '').toLowerCase() !== 'h2h';
  const isRacing = (ev.mode || '').toLowerCase() === 'racing' || ev.isRacing;
  const psId = resolvePayoutStructureId(ev, { isTournament, isRacing });
  if (!psId) return potCents; // fallback: winner take all
  const key = Object.keys(PAYOUT_STRUCTURES).find(k => k.toLowerCase() === psId.toLowerCase());
  const struct = key ? PAYOUT_STRUCTURES[key] : null;
  if (!struct) return potCents;
  const champBand = struct.bands.find(b => b.minRank === 1);
  if (!champBand) return potCents;
  const share = potCents * (champBand.percent / 100);
  return isCrit ? Math.ceil(share) : Math.round(share);
}

export default function StreamOverlayScreen({ route }) {
  const p = route?.params || {};
  const type = (p.type || 'event').toLowerCase();
  const styleRef = useRef(null);

  // --- Inject transparent background (OBS requires this) ---
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const style = document.createElement('style');
    style.id = 'overlay-transparent';
    style.textContent = `
      html, body, #root {
        background: transparent !important;
        background-color: transparent !important;
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
      }
    `;
    document.head.appendChild(style);
    styleRef.current = style;

    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';
    const root = document.getElementById('root');
    if (root) root.style.background = 'transparent';

    return () => {
      if (styleRef.current) document.head.removeChild(styleRef.current);
    };
  }, []);

  if (type === 'subs') return <SubsOverlay p={p} />;
  return <EventOverlay p={p} />;
}

/* ---------- Event overlay (original mode) ---------- */
function EventOverlay({ p }) {
  // --- Params ---
  const id = p.id || p.eventId;
  const coll = (p.collection || p.coll || 'tournaments').toLowerCase();
  const accent = p.accent ? `#${p.accent.replace('#', '')}` : DEFAULT_ACCENT;
  const theme = p.theme || 'dark';
  const position = p.position || 'bottomleft';
  const scale = Math.max(0.5, Math.min(3, Number(p.scale || 1)));

  // --- State ---
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Evergreen step 2.3: settings/economy.critsEnabled (world-readable — this
  // runs as an OBS browser source, usually unauthenticated). Called here,
  // above every early return, so hook order is stable.
  const critsEnabled = useCritsEnabled();

  // --- Real-time event listener ---
  useEffect(() => {
    if (!id) { setLoading(false); setError('No event ID'); return; }
    const unsub = onSnapshot(
      doc(db, coll, id),
      (snap) => {
        if (snap.exists()) {
          setEv({ id: snap.id, ...snap.data() });
          setError(null);
        } else {
          setEv(null);
          setError('Event not found');
        }
        setLoading(false);
      },
      (err) => {
        console.error('Overlay snapshot error:', err);
        setError('Connection lost');
        setLoading(false);
      },
    );
    return () => unsub();
  }, [id, coll]);

  // --- Derived values ---
  // Crit events are entered with non-cash Crits (1 Crit = $1) and reuse the same
  // pool/payout structures. We compute Crit pools from critEntryCost (mirrors
  // LobbyScreen/LiveMatches) and round the pool UP; cash pools round to nearest.
  const isCritEvent = (ev?.entryCurrency === 'crits') || (ev?.critEntryCost != null);
  const critEntryCost = Math.max(0, Math.floor(Number(ev?.critEntryCost || 0)));
  const entryFeeCents = Number(ev?.entryFeeCents || 0);
  const unitCost = isCritEvent ? critEntryCost : entryFeeCents;
  const rakeRate = Math.max(0, Math.min(1, Number(ev?.rakePercent ?? 10) / 100));
  const entrantsCount = Number(ev?.entrantsCount || 0);
  const maxEntrants = Number(ev?.maxEntrants || ev?.bracketSize || ev?.entriesExpected || 0);

  const roundPot = (gross) => (isCritEvent ? Math.ceil(gross) : Math.round(gross));
  const currentPotCents = isCritEvent
    ? roundPot(entrantsCount * unitCost * (1 - rakeRate))
    : (ev?.potCents != null
        ? Number(ev.potCents)
        : Math.round(entrantsCount * entryFeeCents * (1 - rakeRate)));
  const fullPotCents = roundPot(maxEntrants * unitCost * (1 - rakeRate));

  // Currency-aware amount formatter (Crits vs $).
  const fmtAmt = (v) => (isCritEvent ? fmtCrits(v) : fmtUSD(v));
  // Crits OFF hides the Crit pool blocks (cash-event branches untouched).
  const showPool = !(isCritEvent && !critsEnabled);

  // --- Countdown ---
  const regClosesAt = toDateLike(ev?.registrationClosesAt || ev?.registrationCloseTime);
  const countdown = useCountdown(regClosesAt);

  // --- Event status ---
  const statusKey = String(ev?.status || 'scheduled').toLowerCase();

  // Terminal states: show nothing (overlay expires)
  const isExpired = ['completed', 'cancelled', 'canceled', 'closed', 'settled', 'archived'].includes(statusKey);

  // Registration closed? Switch to live mode
  const regClosed = !isExpired && (countdown.done || ['locked', 'seeded', 'live'].includes(statusKey));

  // --- 1st place prize ---
  const firstPlaceCents = useMemo(
    () => getFirstPlaceCents(ev, currentPotCents, isCritEvent),
    [ev, currentPotCents, isCritEvent],
  );

  // --- Positioning ---
  const posStyle = POS_MAP[position] || POS_MAP.bottomleft;

  // --- Title ---
  const title = ev?.requiredStreamTitle || '';

  // --- Theme ---
  const textColor = theme === 'light' ? '#111' : '#FFFFFF';
  const labelColor = theme === 'light' ? '#444' : '#D0D4E0';
  const shadow = theme === 'light'
    ? { textShadow: '0 1px 4px rgba(0,0,0,0.3), 0 0 12px rgba(255,255,255,0.8)' }
    : { textShadow: '0 1px 4px rgba(0,0,0,1), 0 0 12px rgba(0,0,0,0.7)' };

  // --- Loading / error / expired ---
  if (loading || error || !ev || isExpired) {
    return <View style={{ flex: 1, backgroundColor: 'transparent' }} />;
  }

  const fs = (base) => base * scale;

  // --- LIVE MODE: registration closed → show 1st place prize ---
  if (regClosed) {
    return (
      <View style={[styles.container, { position: 'absolute', ...posStyle }]}>
        {!!title && (
          <Text style={[styles.title, { fontSize: fs(13), color: textColor }, shadow]}>
            {title}
          </Text>
        )}
        {showPool ? (
          <>
            <Text style={[styles.label, { fontSize: fs(12), color: labelColor }, shadow]}>
              RACING FOR
            </Text>
            <Text style={[styles.potValue, { fontSize: fs(32), color: accent }, shadow]}>
              {fmtAmt(firstPlaceCents)}
            </Text>
            <Text style={[{ fontSize: fs(12), color: labelColor, fontWeight: '600' }, shadow]}>
              1st Place
            </Text>
          </>
        ) : (
          <Text style={[styles.potValue, { fontSize: fs(32), color: accent }, shadow]}>
            LIVE
          </Text>
        )}
      </View>
    );
  }

  // --- REGISTRATION MODE: show signups, pots, countdown ---
  const countdownText = countdown.d > 0
    ? `${countdown.d}d ${countdown.h}h ${countdown.m}m`
    : countdown.h > 0
      ? `${countdown.h}h ${countdown.m}m ${countdown.sec}s`
      : `${countdown.m}m ${countdown.sec}s`;

  return (
    <View style={[styles.container, { position: 'absolute', ...posStyle }]}>
      {/* Title */}
      {!!title && (
        <Text style={[styles.title, { fontSize: fs(13), color: textColor }, shadow]}>
          {title}
        </Text>
      )}

      {/* Signups */}
      <Text style={[styles.signups, { fontSize: fs(22), color: textColor }, shadow]}>
        {entrantsCount} / {maxEntrants || '?'}{' '}
        <Text style={{ fontSize: fs(13), color: labelColor }}>players</Text>
      </Text>

      {/* Full Pot (dominant) */}
      {showPool && (
      <>
      <Text style={[styles.label, { fontSize: fs(12), color: labelColor }, shadow]}>
        {isCritEvent ? 'FULL CRIT POOL' : 'FULL PRIZE POOL'}
      </Text>
      <Text style={[styles.potValue, { fontSize: fs(28), color: accent }, shadow]}>
        {fmtAmt(fullPotCents)}
      </Text>
      </>
      )}

      {/* Current Pot (secondary, shown only when different from full) */}
      {showPool && currentPotCents !== fullPotCents && (
        <>
          <Text style={[styles.label, { fontSize: fs(11), color: labelColor, marginTop: 2 }, shadow]}>
            {isCritEvent ? 'CURRENT CRIT POOL' : 'CURRENT PRIZE POOL'}
          </Text>
          <Text style={[{ fontSize: fs(18), color: labelColor, fontWeight: '700' }, shadow]}>
            {fmtAmt(currentPotCents)}
          </Text>
        </>
      )}

      {/* Registration Countdown */}
      <Text style={[styles.label, { fontSize: fs(11), color: labelColor, marginTop: 8 }, shadow]}>
        REG CLOSES IN
      </Text>
      <Text style={[{ fontSize: fs(16), color: '#f59e0b', fontWeight: '800' }, shadow]}>
        {countdownText}
      </Text>
    </View>
  );
}

/* ---------- Subscriber overlay ---------- */
// ?type=subs&channel=<handle> (or &uid=<uid>) — live subscriber counter.
// Optional: &goal=N renders a progress meter toward N subs.
// Reads public_profiles (world-readable; count mirrored by toggleStreamFollow).
function SubsOverlay({ p }) {
  const accent = p.accent ? `#${p.accent.replace('#', '')}` : DEFAULT_ACCENT;
  const theme = p.theme || 'dark';
  const position = p.position || 'bottomleft';
  const scale = Math.max(0.5, Math.min(3, Number(p.scale || 1)));
  const goal = Math.max(0, Math.floor(Number(p.goal || 0)));
  const uidParam = p.uid || null;
  const channel = String(p.channel || p.userName || '').trim().toLowerCase();

  const [uid, setUid] = useState(uidParam);
  const [count, setCount] = useState(null);
  const [celebrate, setCelebrate] = useState(false);
  const [latestName, setLatestName] = useState(null);
  const prevSubAtRef = useRef(undefined); // undefined = no snapshot seen yet
  const timerRef = useRef(null);

  // Resolve channel handle → uid (public_profiles is queryable by userNameLower).
  useEffect(() => {
    if (uidParam || !channel) return;
    let alive = true;
    (async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'public_profiles'),
          where('userNameLower', '==', channel),
          limit(1),
        ));
        if (alive && !snap.empty) setUid(snap.docs[0].id);
      } catch (err) {
        console.error('Subs overlay handle lookup failed:', err);
      }
    })();
    return () => { alive = false; };
  }, [uidParam, channel]);

  // Live counter + named flash. The flash keys off latestSubscriber.atMs —
  // written by toggleStreamFollow ONLY on a first-ever follow — rather than
  // count increases, so unsub/resub churn can't retrigger it and the flash
  // always carries the subscriber's public handle.
  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(doc(db, 'public_profiles', uid), (snap) => {
      const d = snap.data() || {};
      setCount(Math.max(0, Number(d.followerCount || 0)));
      const atMs = Number(d.latestSubscriber?.atMs || 0);
      if (prevSubAtRef.current !== undefined && atMs > prevSubAtRef.current) {
        setLatestName(d.latestSubscriber?.name || null);
        setCelebrate(true);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setCelebrate(false), 6000);
      }
      prevSubAtRef.current = atMs;
    });
    return () => {
      unsub();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [uid]);

  if (count == null) {
    return <View style={{ flex: 1, backgroundColor: 'transparent' }} />;
  }

  const posStyle = POS_MAP[position] || POS_MAP.bottomleft;
  const textColor = theme === 'light' ? '#111' : '#FFFFFF';
  const labelColor = theme === 'light' ? '#444' : '#D0D4E0';
  const shadow = theme === 'light'
    ? { textShadow: '0 1px 4px rgba(0,0,0,0.3), 0 0 12px rgba(255,255,255,0.8)' }
    : { textShadow: '0 1px 4px rgba(0,0,0,1), 0 0 12px rgba(0,0,0,0.7)' };
  const fs = (base) => base * scale;
  const goalReached = goal > 0 && count >= goal;
  const goalPct = goal > 0 ? Math.min(100, (count / goal) * 100) : 0;

  return (
    <View style={[styles.container, { position: 'absolute', ...posStyle }]}>
      {celebrate && (
        <>
          <Text style={[styles.subFlash, { fontSize: fs(16), color: accent }, shadow]}>
            NEW FOLLOWER!
          </Text>
          {!!latestName && (
            <Text style={[{ fontSize: fs(15), color: textColor, fontWeight: '800', marginBottom: 4 }, shadow]}>
              {latestName}
            </Text>
          )}
        </>
      )}
      <Text style={[styles.label, { fontSize: fs(12), color: labelColor }, shadow]}>
        FOLLOWERS
      </Text>
      <Text style={[styles.potValue, { fontSize: fs(34), color: celebrate ? accent : textColor }, shadow]}>
        {count}{goal > 0 ? <Text style={{ fontSize: fs(18), color: labelColor }}> / {goal}</Text> : null}
      </Text>
      {goal > 0 && (
        <View style={[styles.goalTrack, { width: fs(180), height: fs(10), borderRadius: fs(5) }]}>
          <View style={{
            width: `${goalPct}%`,
            height: '100%',
            borderRadius: fs(5),
            backgroundColor: accent,
          }} />
        </View>
      )}
      {goalReached && (
        <Text style={[{ fontSize: fs(13), color: accent, fontWeight: '800', marginTop: 4 }, shadow]}>
          GOAL REACHED!
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  subFlash: {
    fontWeight: '900',
    letterSpacing: 1,
    marginBottom: 4,
  },
  goalTrack: {
    backgroundColor: 'rgba(255,255,255,0.25)',
    overflow: 'hidden',
    marginTop: 4,
  },
  container: {
    backgroundColor: 'transparent',
    padding: 16,
  },
  title: {
    fontWeight: '700',
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  signups: {
    fontWeight: '800',
    marginBottom: 8,
  },
  label: {
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontWeight: '600',
    marginBottom: 2,
  },
  potValue: {
    fontWeight: '900',
    marginBottom: 6,
  },
});
